/**
 * Cart Calculation Utilities
 * Used for calculating tax, shipping, and total on the cart page
 */

import type { CartItem } from "@/store/cart";

export interface CartCalculationConfig {
  taxRate?: number;
  shippingFee?: number;
  freeShippingThreshold?: number;
  couponDiscount?: number;
  couponType?: "percentage" | "fixed";
}

export interface CartCalculationResult {
  subtotal: number;
  discount: number;
  discountSavings: number;
  tax: number;
  taxRate: number;
  shipping: number;
  couponDiscount: number;
  total: number;
  itemCount: number;
}

/**
 * Calculate cart subtotal from items
 */
export function calculateCartSubtotal(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

/**
 * Calculate total discount savings (difference between original and discounted prices)
 */
export function calculateDiscountSavings(items: CartItem[]): number {
  return items.reduce((sum, item) => {
    if (item.originalPrice && item.originalPrice > item.price) {
      return sum + (item.originalPrice - item.price) * item.quantity;
    }
    return sum;
  }, 0);
}

/**
 * Calculate tax amount with product-specific VAT support
 * If items have individual VAT rates, those take precedence
 */
export function calculateTax(
  subtotal: number,
  taxRate: number = 0,
  items?: CartItem[]
): number {
  // If items are provided and have specific VAT rates, calculate per-item
  if (items && items.length > 0) {
    const hasProductSpecificVat = items.some(
      (item) => item.vatPercentage !== undefined && item.vatPercentage > 0
    );

    if (hasProductSpecificVat) {
      return items.reduce((totalTax, item) => {
        const itemSubtotal = item.price * item.quantity;
        const itemVatRate = (item.vatPercentage ?? 0) / 100; // Convert percentage to decimal
        return totalTax + itemSubtotal * itemVatRate;
      }, 0);
    }
  }

  // Fallback to global tax rate
  return subtotal * taxRate;
}

/**
 * Calculate shipping fee
 */
export function calculateShipping(
  subtotal: number,
  shippingFee: number = 0,
  freeShippingThreshold: number = 0
): number {
  if (freeShippingThreshold > 0 && subtotal >= freeShippingThreshold) {
    return 0;
  }
  return shippingFee;
}

/**
 * Calculate coupon discount
 */
export function calculateCouponDiscount(
  subtotal: number,
  couponValue: number = 0,
  couponType: "percentage" | "fixed" = "fixed"
): number {
  if (couponType === "percentage") {
    return subtotal * (couponValue / 100);
  }
  return Math.min(couponValue, subtotal);
}

/**
 * Calculate total item count
 */
export function calculateItemCount(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * Calculate complete cart totals
 */
export function calculateCartTotals(
  items: CartItem[],
  config: CartCalculationConfig = {}
): CartCalculationResult {
  const {
    taxRate = 0,
    shippingFee = 0,
    freeShippingThreshold = 0,
    couponDiscount = 0,
    couponType = "fixed",
  } = config;

  // Calculate base values
  const subtotal = calculateCartSubtotal(items);
  const discountSavings = calculateDiscountSavings(items);
  const itemCount = calculateItemCount(items);

  // Calculate coupon discount
  const coupon = calculateCouponDiscount(subtotal, couponDiscount, couponType);

  // Calculate subtotal after coupon
  const subtotalAfterCoupon = subtotal - coupon;

  // Calculate tax with product-specific VAT support
  const tax = calculateTax(subtotalAfterCoupon, taxRate, items);

  // Calculate shipping
  const shipping = calculateShipping(
    subtotalAfterCoupon,
    shippingFee,
    freeShippingThreshold
  );

  // Calculate final total
  const total = subtotalAfterCoupon + tax + shipping;

  return {
    subtotal,
    discount: discountSavings,
    discountSavings,
    tax,
    taxRate,
    shipping,
    couponDiscount: coupon,
    total,
    itemCount,
  };
}

/**
 * Format price for display with currency icon
 * Simply formats the number and adds the currency icon from API
 */
export function formatPrice(
  amount: number,
  currencyIcon: string = "£"
): string {
  return `${currencyIcon} ${amount.toFixed(2)}`;
}

/**
 * Check if cart qualifies for free shipping
 */
export function qualifiesForFreeShipping(
  subtotal: number,
  freeShippingThreshold: number
): boolean {
  return freeShippingThreshold > 0 && subtotal >= freeShippingThreshold;
}

/**
 * Calculate remaining amount for free shipping
 */
export function remainingForFreeShipping(
  subtotal: number,
  freeShippingThreshold: number
): number {
  if (qualifiesForFreeShipping(subtotal, freeShippingThreshold)) {
    return 0;
  }
  return Math.max(0, freeShippingThreshold - subtotal);
}
